import { call, delay, select, takeLatest } from 'redux-saga/effects';
import { viewerRpcScheme as viewerRpc } from 'src/services/rpc/viewer/scheme';
import * as apiTaskExecutions from '@api/qcTaskExecutions';
import { actions } from './QCTaskExecutions.slice';
import { Resp, dispatch } from '../helpers';
import { runToast } from '../tools';
import {
  QCTaskExecutionFetchRequestAction,
  QCTaskExecutionExportResultFetchRequestAction,
  StatusResult,
} from '@restore/QCTaskExecutions/QCTaskExecutions.types';
import { resolveFormCall, rejectFormCall } from 'src/services/rpc/pendingCalls';
import {
  ExecutionStatus,
  ExecutionType,
} from '@dynamika-shared/types/executions';
import { selectQCTaskState } from '@restore/QCTask/QCTask.slice';
import { selectClinicalTrialContextState } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { prepareQCStudyState } from '../DICOMViewer/modes/QC/QC.selector';
import {
  QC_STATUS_MESSAGES,
  isViewerProcessExecution,
} from './QCTaskExecutions.helpers';

const pollingInterval = 5_000;

// Prevent duplicate polling loops
const activePollingKeys = new Set<string>();

export function* fetchQCTaskExecutionsWorker(
  action: QCTaskExecutionFetchRequestAction
) {
  try {
    const data: Resp<typeof apiTaskExecutions.fetchTaskExecutions> = yield call(
      apiTaskExecutions.fetchTaskExecutions,
      action.payload.clinicalTrialId,
      action.payload.taskId
    );

    const processes = data.filter(isViewerProcessExecution);

    yield dispatch(actions.fetchQCTaskExecutionsSuccess(processes));
    resolveFormCall(action.payload.__callId, processes);
  } catch (error) {
    yield call(runToast, 'error', 'Error getting processes list');
    yield dispatch(
      actions.fetchQCTaskExecutionsRequestFailure(
        'fetchQCTaskExecutionsWorker error'
      )
    );
    rejectFormCall(action.payload.__callId, error);
  }
}

export function* fetchQCTaskExecutionsExportResultWorker(
  action: QCTaskExecutionExportResultFetchRequestAction
) {
  try {
    const {
      visits,
      task,
      clinicalTrialCode,
      clinicalTrialId,
    }: ReturnType<typeof prepareQCStudyState> =
      yield select(prepareQCStudyState);
    const currentVisit = visits.find(v => v.taskId === task?.id);

    const {
      data,
    }: Resp<typeof apiTaskExecutions.fetchQCTaskExecutionsExportResult> =
      yield call(
        apiTaskExecutions.fetchQCTaskExecutionsExportResult,
        action.payload.clinicalTrialId ?? clinicalTrialId,
        action.payload.taskId,
        action.payload.taskExecutionInstanceId
      );

    const response: Response = yield call(fetch, data.signedUrl, {
      credentials: 'omit',
    });
    if (!response.ok) {
      yield call(
        runToast,
        'error',
        `Failed to fetch file: ${response.statusText}`
      );
      rejectFormCall(action.payload.__callId, new Error(response.statusText));
      return;
    }

    const blob: Blob = yield call([response, response.blob]);
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    const fallbackFileName = `${clinicalTrialCode}_${task?.patientCode}_${currentVisit?.visitName}.zip`;
    link.download = action.payload.fileName || fallbackFileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);

    resolveFormCall(action.payload.__callId, {
      ...data,
      taskExecutionInstanceId: action.payload.taskExecutionInstanceId,
    });
  } catch (error) {
    yield call(runToast, 'error', 'Error export file');
    rejectFormCall(action.payload.__callId, error);
  }
}

const getStatusChangedText = (
  type: ExecutionType,
  name: string,
  status: ExecutionStatus
) => {
  if (type === ExecutionType.QC_EXPORT || type === ExecutionType.QC_IMPORT) {
    return QC_STATUS_MESSAGES[type][status as StatusResult];
  }

  let text = 'status changed';
  if (status === ExecutionStatus.COMPLETED) {
    text = 'has been executed successfully';
  }
  if (status === ExecutionStatus.FAILED) {
    text = 'has been failed';
  }
  return `${name || 'Process'} ${text}. Please reload the page.`;
};

export function* executionsLongPollingWorker() {
  const previousStatuses: Record<string, ExecutionStatus | null> = {};
  const { data: taskData }: ReturnType<typeof selectQCTaskState> =
    yield select(selectQCTaskState);

  const {
    data: clinicalTrialData,
  }: ReturnType<typeof selectClinicalTrialContextState> = yield select(
    selectClinicalTrialContextState
  );

  if (!taskData && !clinicalTrialData?.configuration) {
    return;
  }

  const key = `${clinicalTrialData!.configuration.id}:${taskData!.id}`;
  if (activePollingKeys.has(key)) {
    // polling for this context is already running
    return;
  }
  activePollingKeys.add(key);

  try {
    while (true) {
      try {
        const response: Resp<typeof apiTaskExecutions.fetchTaskExecutions> =
          yield call(
            apiTaskExecutions.fetchTaskExecutions,
            clinicalTrialData!.configuration.id,
            taskData!.id
          );

        const processes = response.filter(isViewerProcessExecution);

        for (const item of processes) {
          const executionKey = item.id ?? item.executionId;

          const prevStatus = previousStatuses[executionKey] ?? null;

          if (prevStatus !== item.status) {
            if (prevStatus) {
              const message = getStatusChangedText(
                item.type,
                item.attributes?.name,
                item.status
              );

              const isCompleted = item.status === ExecutionStatus.COMPLETED;
              if (item.type === ExecutionType.QC_EXPORT) {
                if (isCompleted) {
                  // gather filename parts to avoid relying on current context later
                  const {
                    visits,
                    task,
                    clinicalTrialCode,
                  }: ReturnType<typeof prepareQCStudyState> =
                    yield select(prepareQCStudyState);
                  const currentVisit = visits.find(v => v.taskId === task?.id);
                  const fileName = `${clinicalTrialCode}_${task?.patientCode}_${currentVisit?.visitName}.zip`;

                  yield call(runToast, 'readyDownload', message, '', {
                    autoClose: false,
                    data: {
                      clinicalTrialId: clinicalTrialData!.configuration.id,
                      taskId: taskData!.id,
                      taskExecutionInstanceId: item.id,
                      fileName,
                    },
                  });
                } else {
                  yield call(runToast, 'error', message, '', {
                    autoClose: false,
                  });
                }
              } else {
                yield call(
                  runToast,
                  isCompleted ? 'info' : 'error',
                  message,
                  '',
                  {
                    autoClose: false,
                  }
                );
              }
            }
            previousStatuses[executionKey] = item.status;
          }
        }

        const isPendingItems = processes.some(
          item => item.status === ExecutionStatus.IN_PROGRESS
        );

        yield call(viewerRpc.events.processesPolled, processes);

        if (!isPendingItems) break;

        yield delay(pollingInterval);
      } catch (error) {
        yield call(runToast, 'error', 'Error getting existing processes');
        break;
      }
    }
  } finally {
    activePollingKeys.delete(key);
  }
}

export function* QCTaskExecutionsSaga() {
  yield takeLatest(
    actions.fetchQCTaskExecutionsRequest.type,
    fetchQCTaskExecutionsWorker
  );

  yield takeLatest(
    actions.downloadExportFile.type,
    fetchQCTaskExecutionsExportResultWorker
  );

  yield takeLatest(actions.pollExecutions.type, executionsLongPollingWorker);
}
